import type { AxiosError, InternalAxiosRequestConfig } from 'axios'

/**
 * Подразделение и коды отказов бэкенда. Пользователь с правом
 * subdivisions:choose-* смотрит чужое подразделение, отправляя его в
 * заголовке; бэкенд учитывает заголовок только для GET и сам проверяет, что
 * подразделение доступно.
 *
 * Адаптер не знает про сторы, роутер и тосты: источник значения и реакции на
 * отказы регистрирует приложение (`boot/subdivision`).
 */
export const SUBDIVISION_HEADER = 'X-Subdivision-Id'

/** Отказ в доступе: нет права на действие или действие только для сотрудников. */
export type AccessDeniedCode = 'forbidden' | 'no_access' | 'employee_required'

interface ErrorBody {
  code?: string
}

interface Handlers {
  /** Значение заголовка или null — не отправлять. */
  resolveSubdivisionId: () => string | null
  /** Подразделение не выбрано (409) или недоступно (403) — выбрать заново. */
  onSubdivisionRejected: () => void
  onAccessDenied: (code: AccessDeniedCode) => void
}

const ACCESS_DENIED_CODES: readonly string[] = ['forbidden', 'no_access', 'employee_required']

let handlers: Handlers = {
  resolveSubdivisionId: () => null,
  onSubdivisionRejected: () => {},
  onAccessDenied: () => {},
}

/** Ошибки, о которых пользователю уже сообщили здесь, — общий обработчик их пропускает. */
const handledErrors = new WeakSet<object>()

export function provideSubdivision(value: Handlers) {
  handlers = value
}

export function isHandledAccessError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && handledErrors.has(error)
}

export function subdivisionInterceptor(config: InternalAxiosRequestConfig) {
  const subdivisionId = handlers.resolveSubdivisionId()

  if (subdivisionId) {
    config.headers[SUBDIVISION_HEADER] = subdivisionId
  }

  return config
}

export function subdivisionRejectHandler(error: AxiosError<ErrorBody>) {
  const status = error.response?.status
  const code = error.response?.data?.code ?? ''

  if (
    (status === 409 && code === 'subdivision_required')
    || (status === 403 && code === 'subdivision_forbidden')
  ) {
    handledErrors.add(error)
    handlers.onSubdivisionRejected()
  } else if (status === 403 && ACCESS_DENIED_CODES.includes(code)) {
    handledErrors.add(error)
    handlers.onAccessDenied(code as AccessDeniedCode)
  }

  return Promise.reject(error)
}
